import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
  Stack,
  Alert,
  Chip,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Info } from '@mui/icons-material';

export interface VisibilitySettings {
  isPublic: boolean;
  allowComments?: boolean;
  allowDownloads?: boolean;
  requireApproval?: boolean;
}

interface VisibilityControlsProps {
  value: VisibilitySettings;
  onChange: (settings: VisibilitySettings) => void;
  showAdvanced?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  title?: string;
  subtitle?: string;
}

export const VisibilityControls: React.FC<VisibilityControlsProps> = ({
  value,
  onChange,
  showAdvanced = false,
  disabled = false,
  error,
  helperText,
  title = "Visibility Settings",
  subtitle = "Control who can see and interact with this content"
}) => {
  const handleChange = (field: keyof VisibilitySettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      [field]: event.target.checked
    });
  };

  return (
    <Box>
      <Stack spacing={2}>
        {/* Header */}
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="subtitle2">
            {title}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {subtitle}
          </Typography>
        </Box>

        {/* Basic Visibility Toggle */}
        <FormControl error={Boolean(error)} fullWidth>
          <FormControlLabel
            control={
              <Switch
                checked={value.isPublic}
                color="primary"
                disabled={disabled}
                onChange={handleChange('isPublic')}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {value.isPublic ? <Visibility color="primary" /> : <VisibilityOff color="action" />}
                <Typography variant="body2">
                  {value.isPublic ? 'Public' : 'Private'}
                </Typography>
                <Tooltip title={value.isPublic ? 'Visible to all community members' : 'Only visible to you and administrators'}>
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
          {error ? <FormHelperText error>{error}</FormHelperText> : null}
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>

        {/* Status Indicator */}
        <Box>
          <Chip
            color={value.isPublic ? 'success' : 'default'}
            icon={value.isPublic ? <Visibility color="inherit" /> : <VisibilityOff color="inherit" />}
            label={value.isPublic ? 'Public Document' : 'Private Document'}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Advanced Options */}
        

        {/* Info Alert */}
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> {value.isPublic 
              ? 'Public documents are visible to all community members and may appear in search results.'
              : 'Private documents are only visible to you and community administrators.'
            }
          </Typography>
        </Alert>
      </Stack>
    </Box>
  );
};

export default VisibilityControls;
